import React, { useEffect, useLayoutEffect, useState, useMemo } from "react"
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import Navbar from "../../layout/Navbar"
import Footer from "../../layout/Footer"
import UpdateCabForm from "../Cab/UpdateCabForm"
import MainCab from "../Cab/MainCab"
import CityPairView from "./CityPairView"
import SEO from "../../components/SEO"
import { slugify } from "../../utils/slugify"
import {
  reconcileStorageForRoutePage,
  tripDataMatchesRouteSlugs,
  readTripDataParsed,
} from "../../utils/clientStorage"
import { cabAction } from "../../store/Cab/CAbReducer"
import ProgressStepper from "../../components/ProgressStepper"

const CityPairPage = ({ fromCity: fromCityProp, toCity: toCityProp }) => {
  const params = useParams()
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const [searchParams] = useSearchParams()
  const roundTripParam = parseInt(searchParams.get("round_trip")) || 0

  // Parse routePath from /routes/:routePath format (e.g., "surat-to-daman-cab")
  const parseRoutePath = (routePath) => {
    if (!routePath) return { fromCity: null, toCity: null }
    // Remove -cab suffix if present
    const pathWithoutCab = routePath.replace(/-cab$/, '')
    const parts = pathWithoutCab.split('-to-')
    if (parts.length === 2) {
      return { fromCity: parts[0], toCity: parts[1] }
    }
    return { fromCity: null, toCity: null }
  }

  // Use props if provided (from query params), otherwise parse from route params
  const routeParams = params.routePath ? parseRoutePath(params.routePath) : {}
  const fromCity = fromCityProp || routeParams.fromCity
  const toCity = toCityProp || routeParams.toCity
  const dispatch = useDispatch()
  const { location } = useSelector((state) => state.location)
  const [fromCityData, setFromCityData] = useState(null)
  const [toCityData, setToCityData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [shouldRedirectToHome, setShouldRedirectToHome] = useState(false)
  const [routeStorageEpoch, setRouteStorageEpoch] = useState(0)

  // Before paint: reconcile storage + redirect home if tripData does not match this route URL
  useLayoutEffect(() => {
    if (!fromCity || !toCity) return
    reconcileStorageForRoutePage(fromCity, toCity)
    dispatch(cabAction.resetCabBookingContext())

    const parsed = readTripDataParsed()
    const hasMatchingFormData = tripDataMatchesRouteSlugs(parsed, fromCity, toCity)

    if (!hasMatchingFormData) {
      setShouldRedirectToHome(true)
      navigate("/", { replace: true, state: { routePrefill: { fromCity, toCity } } })
    }
    setRouteStorageEpoch((n) => n + 1)
  }, [fromCity, toCity, dispatch, navigate])

  // Fetch both city APIs
  useEffect(() => {
    let ignore = false

    async function loadCities() {
      setLoading(true)
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://customerapi.pulpitcabs.com"
        
        const [fromRes, toRes] = await Promise.allSettled([
          fetch(`${backendUrl}/website/get_city_pages`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              city_slug: fromCity.toLowerCase(),
            }),
          }),
          fetch(`${backendUrl}/website/get_city_pages`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              city_slug: toCity.toLowerCase(),
            }),
          }),
        ])

        if (!ignore) {
          if (fromRes.status === "fulfilled" && fromRes.value.ok) {
            const fromJson = await fromRes.value.json()
            if (fromJson?.data?.city_data) {
              setFromCityData(fromJson.data.city_data)
            }
          }
          if (toRes.status === "fulfilled" && toRes.value.ok) {
            const toJson = await toRes.value.json()
            if (toJson?.data?.city_data) {
              setToCityData(toJson.data.city_data)
            }
          }
          setLoading(false)
        }
      } catch (e) {
        if (!ignore) {
          console.error("Error loading city data:", e)
          setLoading(false)
        }
      }
    }

    loadCities()
    return () => {
      ignore = true
    }
  }, [fromCity, toCity])

  // Format city names for display
  const formatCityName = (slug) => {
    if (!slug) return ""
    return slug
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const fromCityName = useMemo(
    () => fromCityData?.cityName || formatCityName(fromCity),
    [fromCityData, fromCity]
  )
  const toCityName = useMemo(
    () => toCityData?.cityName || formatCityName(toCity),
    [toCityData, toCity]
  )

  // Immediately update localStorage when route params change (only if trip already matches this route)
  useEffect(() => {
    if (fromCity && toCity) {
      const parsedStoredData = readTripDataParsed() || {}
      if (!tripDataMatchesRouteSlugs(parsedStoredData, fromCity, toCity)) {
        return
      }

      // Get formatted city names (use API data if available, otherwise format from slug)
      const currentFromCityName = fromCityName || formatCityName(fromCity)
      const currentToCityName = toCityName || formatCityName(toCity)
      
      // Check if route has changed by comparing city slugs (most reliable)
      // When navigating via route URL, always update to match the new route
      const previousFromSlug = slugify(parsedStoredData.pickup_city || "")
      const previousToSlug = slugify(parsedStoredData.drop_city || "")      
      
      const routeChanged = previousFromSlug !== fromCity || previousToSlug !== toCity
      
      // Fix: Only overwrite if the URL route params actually changed, don't overwrite user edits!
      if (routeChanged) {
        const tripData = {
          ...parsedStoredData,
          trip_type: 3, 
          round_trip: parsedStoredData.round_trip ?? roundTripParam, 
          pickup: currentFromCityName,
          drop: currentToCityName,
          pickup_city: currentFromCityName, 
          drop_city: currentToCityName, 
          pickup_lat: null,
          pickup_long: null,
          drop_lat: null,
          drop_long: null,
          trip_date: parsedStoredData.trip_date || (() => {
            const tomorrow = new Date()
            tomorrow.setDate(tomorrow.getDate() + 1)
            return tomorrow.toISOString().split("T")[0]
          })(),
          return_date: parsedStoredData.return_date || (() => {
            if (parsedStoredData.round_trip === 1 || roundTripParam === 1) {
              const date = new Date()
              date.setDate(date.getDate() + 3)
              return date.toISOString().split("T")[0]
            }
            return ""
          })(),
          return_time: parsedStoredData.return_time || (() => {
            if (parsedStoredData.round_trip === 1 || roundTripParam === 1) return "10:00"
            return ""
          })(),
          trip_time: parsedStoredData.trip_time || "10:00",
          package_type_in_hours: parsedStoredData.package_type_in_hours || "",
          phone_number: parsedStoredData.phone_number || "",
          ip_address: location?.ip || parsedStoredData.ip_address || "8425",
        }
        
        localStorage.setItem("tripData", JSON.stringify(tripData))
        window.dispatchEvent(new CustomEvent('tripDataUpdated'))
      }
    }
  }, [fromCity, toCity, fromCityName, toCityName, location, routeStorageEpoch])

  // Set up tripDetails - use localStorage if available (from query params), otherwise use defaults
  const tripDetails = useMemo(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    const defaultDateEarly = tomorrow.toISOString().split("T")[0]
    if (routeStorageEpoch === 0) {
      const returnDate = (() => {
        const date = new Date()
        date.setDate(date.getDate() + 3)
        return date.toISOString().split("T")[0]
      })()

      return {
        trip_type: 3,
        round_trip: roundTripParam,
        pickup: fromCityName,
        drop: toCityName,
        pickup_city: fromCityName,
        drop_city: toCityName,
        trip_date: defaultDateEarly,
        trip_time: "10:00",
        return_date: roundTripParam === 1 ? returnDate : "",
        return_time: roundTripParam === 1 ? "10:00" : "",
        phone_number: "",
        package_type_in_hours: "",
        ip_address: location?.ip || "8425",
        pickup_lat: null,
        pickup_long: null,
        drop_lat: null,
        drop_long: null,
      }
    }

    const storedTripData = localStorage.getItem("tripData")
    const parsedStoredData = storedTripData ? JSON.parse(storedTripData) : {}

    // Check if route has changed by comparing city names
    const routeChanged = 
      parsedStoredData.pickup_city !== fromCityName || 
      parsedStoredData.drop_city !== toCityName

    // If we have stored data with trip details, use it (preserves query params)
    if (parsedStoredData.trip_type === 3 && (parsedStoredData.round_trip === 0 || parsedStoredData.round_trip === 1)) {
      return parsedStoredData
    }

    // Otherwise use defaults
    const defaultTime = "10:00"

    const tripData = {
      trip_type: 3, // Outstation
      round_trip: parsedStoredData.round_trip ?? roundTripParam, // Use query param if no stored data
      pickup: fromCityName, // Always use current route city name
      drop: toCityName, // Always use current route city name
      pickup_city: fromCityName, // City name only
      drop_city: toCityName, // City name only
      trip_date: parsedStoredData.trip_date || defaultDateEarly,
      trip_time: parsedStoredData.trip_time || defaultTime,
      return_date: parsedStoredData.return_date || (() => {
        if (parsedStoredData.round_trip === 1 || roundTripParam === 1) {
          const date = new Date()
          date.setDate(date.getDate() + 3)
          return date.toISOString().split("T")[0]
        }
        return ""
      })(),
      return_time: parsedStoredData.return_time || (() => {
        if (parsedStoredData.round_trip === 1 || roundTripParam === 1) return "10:00"
        return ""
      })(),
      package_type_in_hours: parsedStoredData.package_type_in_hours || "",
      phone_number: parsedStoredData.phone_number || "",
      ip_address: location?.ip || parsedStoredData.ip_address || "8425",
      // Clear lat/long for new route
      pickup_lat: null,
      pickup_long: null,
      drop_lat: null,
      drop_long: null,
    }

    // Do not persist here — reconcile effect already cleared mismatching data; avoid repopulating before home redirect
    return tripData
  }, [fromCityName, toCityName, location, routeStorageEpoch, routerLocation.key])

  // SEO data
  const seoTitle = `${fromCityName} to ${toCityName} Cab | One Way Outstation`
  const seoDescription = `Book one-way outstation cab from ${fromCityName} to ${toCityName}. Transparent pricing, verified drivers, clean cars. Best rates for ${fromCityName} to ${toCityName} route.`
  const canonicalUrl = `${window.location.origin}/routes/${slugify(fromCity)}-to-${slugify(toCity)}-cab`

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [fromCity, toCity])

  if (shouldRedirectToHome) return null

  const routeContentReady =
    routeStorageEpoch > 0 && tripDataMatchesRouteSlugs(readTripDataParsed(), fromCity, toCity)

  return (
    <div className="cityPair">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={`${fromCityName} to ${toCityName} cab, ${fromCityName} ${toCityName} taxi, one way cab ${fromCityName} ${toCityName}, outstation cab ${fromCityName} ${toCityName}`}
        canonical={canonicalUrl}
      />
      <Navbar />
      <ProgressStepper currentStep={2} />
      {routeContentReady ? (
        <>
          <UpdateCabForm />
          <MainCab tripDetails={tripDetails} />
        </>
      ) : null}
      <CityPairView
        fromCity={fromCity}
        toCity={toCity}
        fromCityName={fromCityName}
        toCityName={toCityName}
        fromCityData={fromCityData}
        toCityData={toCityData}
        loading={loading}
      />
      <Footer />
    </div>
  )
}

export default CityPairPage

